import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { debounceTime, EMPTY, forkJoin, Observable, Subject, Subscription, switchMap, tap } from 'rxjs';
import { IdsForSettingsService } from 'src/app/components/settings/ids-for-settings.service';
import { ExternalContentProviders, ExternalContentProviderSource } from 'src/app/constants/ExternalContentProviders.enum';
import { Crumb } from 'src/app/models/crumb';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ExternalCloudLocationsApiClient } from 'src/app/services/api-clients/external-cloudlocations.api-client';
import { MsgraphApiClient } from 'src/app/services/external/msgraph.api-client';
import { LoggingService } from 'src/app/services/logging.service';
import { ToastService } from 'src/app/services/toast.service';
import { DossierTypeChecklistItemDto } from 'src/app/models/hyarchisDossierTypeChecklistItem';
import CloudItem from 'src/app/models/clouditem';
import { DocumentChecklistDto } from 'src/app/models/hyarchisChecklist';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { StoreDocumentsHyarchisDto } from 'src/app/models/hyarchisStoreDocument';
import { ExportRequestModel } from 'src/app/services/external-cloudlocations.service';
import { BuilderRequestAutoExportSettingsModel } from 'src/app/models/builder/builder-request-auto-export-settings.model';
import { BuilderRequestApiClient } from 'src/app/services/api-clients/builder-request.api-client';
import { BuilderRequestModel } from 'src/app/models/builder/builder-request.model';
import { StatusRequestModel } from 'src/app/models/status/status-request.model';
import { StatusRequestApiClient } from 'src/app/services/api-clients/status-request.api-client';
import { SourceViews } from 'src/app/constants/source-views';
import { ExportMethods } from 'src/app/constants/export-methods.enum';
import CloudLocation from 'src/app/models/cloudlocations';

@Injectable()
export class CloudDocumentService implements OnDestroy {
  /** Whether the modal is opened in export mode (true) or import/upload mode (false). */
  isExportMode = false;
  /** Whether the export is being configured for auto-export (scheduled/recurring). */
  isForAutoExport = false;
  /** ID of the request being exported; empty string when not in export mode. */
  requestId = '';
  /** The builder or status request associated with this export, if any. */
  request: BuilderRequestModel | StatusRequestModel | null = null;

  // ── State ──────────────────────────────────────────────────────────────────

  /** GUID of the currently authenticated user. */
  userGuid = '';
  /** The active cloud provider (e.g. MsGraph, Hyarchis). */
  provider!: ExternalContentProviders;
  /** The active source within the provider (e.g. SharePoint, OneDrive). */
  source!: ExternalContentProviderSource;
  /** Display name of the currently selected provider, used in toasts and labels. */
  cloudProviderName = '';

  /** Whether the organisation has the SharePoint plugin enabled. */
  hasSharePoint = false;
  /** Whether the organisation has the OneDrive plugin enabled. */
  hasOneDrive = false;
  /** Whether the organisation has the Teams plugin enabled. */
  hasTeams = false;
  /** Whether the organisation has the Hyarchis plugin enabled. */
  hasHyarchis = false;
  /** Whether the organisation has the AFAS plugin enabled. */
  hasAfas = false;
  /** Whether the organisation has the Pinkweb Dossier plugin enabled. */
  hasPinkwebdossier = false;
  /** Whether the user has selected a provider and the file browser should be shown. */
  hasSelectedProvider = false;

  /** Full folder structure returned by the provider, used to render the sidebar tree. */
  folderTree: any;
  /** Flat list of items in the current folder, used to render the grid or list view. */
  folderGrid: any;
  /** Files selected by the user for import. */
  selectedFiles: any[] = [];
  /** True while a folder/provider request is in flight; disables navigation controls. */
  isNavigating = false;
  /** True until the first provider data has been successfully loaded. */
  isLoading = true;
  /** True when the provider returned an error (e.g. not authorised). */
  errorOccured = false;

  /** Breadcrumb trail representing the current folder path; empty means root. */
  selectedDirectory: Crumb[] = [];
  /** ID of the folder currently selected for export; null when nothing is selected. */
  selectedExportId: string | null = null;
  /** Whether the file browser is displayed as a grid or list. */
  selectedViewType: SourceViews = SourceViews.Grid;
  /** Whether the details panel on the right is collapsed; when true the file browser spans full width. */
  detailsCollapsed = false;

  /** The chosen export method (Files, Folder, or Zip). */
  selectedOption: ExportMethods = ExportMethods.Files;
  /** File or zip name entered by the user for the export. */
  fileName = '';
  /** Available checklist items fetched from Hyarchis for the current folder. */
  checklistItems: DossierTypeChecklistItemDto[] = [];
  /** Documents to be exported to Hyarchis, mapped to checklist items. */
  documentsToExport: DocumentChecklistDto[] = [];

  /** Current value of the search input; updated on every keystroke. */
  searchTerm = '';
  /** OAuth redirect URL used during provider authorisation flows. */
  url = '';
  /** Resolved translation strings keyed by their logical name. */
  translations: any = {};

  /** Whether multiple files can be selected simultaneously in import mode. */
  allowMultiselect = true;

  /** Available export method options shown in the export method dropdown. */
  exportMethods = [
    { value: ExportMethods.Files, label: 'Files', enabled: true },
    { value: ExportMethods.Folder, label: 'Folder', enabled: true },
    { value: ExportMethods.Zip, label: 'Zip', enabled: true }
  ];

  /** Emits each time the user confirms an import; the upload modal forwards these as outputs. */
  readonly importFiles$ = new Subject<{ provider: string, id: string, externalFiles: any, name: string }>();
  /** Emits a folder ID after navigation so the sidebar can scroll that node into view. */
  readonly scrollToNode$ = new Subject<string>();

  // ── Private ────────────────────────────────────────────────────────────────

  /** SharePoint site ID of the currently open site; used to scope subsequent API calls. */
  private currentSite = '';
  /** ID of the parent folder of the current view; used to navigate up one level. */
  private parentFolderId = '';
  /** ID of the folder currently open in the grid view. */
  private currentFolderId = '';


  /** ID of the organisation currently active in settings; triggers a settings reload on change. */
  private selectedOrganisationId = '';
  /** Reserved for future per-organisation settings; not actively used. */
  private orgSettings: any = {};
  /** Debounce subject for the search input; fires searchCurrent after 800 ms of inactivity. */
  private searchSubject = new Subject<string>();
  /** Subscription to the active organisation ID; cleaned up on destroy. */
  private organisationIdSubscription: Subscription;

  /**
   * Whether the 'Hyarchis' has been selected as provider
   */
  get isHyarchisSource() { return this.source === ExternalContentProviderSource.Hyarchis; }

  constructor(
    private authenticationService: AuthenticationService,
    public activeModal: NgbActiveModal,
    private cloudLocationsApiClient: ExternalCloudLocationsApiClient,
    private toastService: ToastService,
    private translateService: TranslateService,
    private translateParser: TranslateParser,
    private loggingService: LoggingService,
    private msgraphApiClient: MsgraphApiClient,
    private idsForSettingsService: IdsForSettingsService,
    private localStorageService: LocalStorageService,
    private builderRequestApiClient: BuilderRequestApiClient,
    private statusRequestApiClient: StatusRequestApiClient,
  ) {
    this.getTranslations();
    translateService.onLangChange.subscribe(() => this.getTranslations());

    this.authenticationService.currentUser.subscribe(data => {
      this.userGuid = data.sub;
    });

    this.searchSubject.pipe(
      debounceTime(1000),
      switchMap(value => this.searchCurrent(value))
    ).subscribe();

    this.organisationIdSubscription = this.idsForSettingsService.selectedOrganisationId$.subscribe(organisationId => {
      if (organisationId === this.selectedOrganisationId) return;
      this.selectedOrganisationId = organisationId;
    });

    this.selectView(this.localStorageService.SourceViewType || SourceViews.Grid);
  }

  ngOnDestroy(): void {
    this.organisationIdSubscription?.unsubscribe();
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  /** Configures the service from the modal's inputs; must be called in ngOnInit before any interaction. */
  init(config: {
    isExportMode: boolean;
    isForAutoExport?: boolean;
    requestId?: string;
    enabledPlugins: any[];
    fileName?: string;
    request?: BuilderRequestModel | StatusRequestModel | null;
    allowMultiselect?: boolean;
  }) {
    this.isExportMode = config.isExportMode;
    this.isForAutoExport = config.isForAutoExport ?? false;
    this.requestId = config.requestId ?? '';
    this.fileName = config.fileName ?? '';
    this.request = config.request ?? null;
    this.allowMultiselect = config.allowMultiselect ?? true;

    const plugins = config.enabledPlugins ?? [];
    this.hasSharePoint = plugins.some((p: any) => p.code === 'SharePoint');
    this.hasHyarchis = plugins.some((p: any) => p.code === 'Hyarchis');
    this.hasOneDrive = plugins.some((p: any) => p.code === 'OneDrive');
    this.hasTeams = plugins.some((p: any) => p.code === 'Teams');
    this.hasAfas = plugins.some((p: any) => p.code === 'AFASInSite');
    this.hasPinkwebdossier = plugins.some((p: any) => p.code === 'PinkwebDossier');
  }

  // ── Navigatie ──────────────────────────────────────────────────────────────

  /** Closes the modal without returning a value. */
  closeDialog() {
    this.activeModal.close();
  }

  /** Switches the file browser between grid and list view and persists the choice to local storage. */
  selectView(viewType: SourceViews) {
    this.selectedViewType = viewType;
    this.localStorageService.SourceViewType = viewType;
  }

  /** Toggles the details panel open or closed. */
  toggleCloudDetails() {
    this.detailsCollapsed = !this.detailsCollapsed;
  }

  /** Called on every search input change; feeds the debounce subject. */
  onSearchChange(value: string) {
    this.searchSubject.next(value);
  }

  /** Returns only the folder items from folderTree for rendering the sidebar tree. */
  getFoldersForTree(): any[] {
    if (!this.folderTree) return [];
    return this.folderTree.filter((item: any) => item.isFolder && !item.parent);
  }

  /** Returns all items in the current folder for rendering the grid or list view. */
  getFoldersForGrid(): any[] {
    return this.folderGrid ?? [];
  }

  /** Returns true when the given folder is the deepest open folder in the breadcrumb trail. */
  isFolderActive(folderId: string): boolean {
    if (this.selectedDirectory.length === 0) return false;
    return this.selectedDirectory[this.selectedDirectory.length - 1]?.id === folderId;
  }

  /** Returns true when the given folder appears anywhere in the current breadcrumb trail. */
  isFolderOpened(folderId: string): boolean {
    return this.selectedDirectory.some(item => item.id === folderId);
  }

  /** Switches the active provider/source, resets folder state and triggers authorisation check. */
  changeProvider(provider: ExternalContentProviders, source: ExternalContentProviderSource) {
    this.isNavigating = true;
    this.folderTree = [];
    this.folderGrid = [];
    this.selectedFiles = [];
    this.provider = provider;
    this.source = source;
    this.parentFolderId = '';
    this.currentSite = '';
    this.currentFolderId = '';
    this.searchTerm = '';
    this.removeAllCrumbs();

    if (this.provider === ExternalContentProviders.Afas) {
      this.cloudProviderName = this.translations.Afas;
    } else if (this.provider === ExternalContentProviders.PinkwebDossier) {
      this.cloudProviderName = this.translations.Pinkwebdossier;
    }

    switch (this.source) {
      case ExternalContentProviderSource.OneDrive:
        this.cloudProviderName = this.translations.OneDrive; break;
      case ExternalContentProviderSource.SharePoint:
        this.cloudProviderName = this.translations.SharePoint; break;
      case ExternalContentProviderSource.Teams:
        this.cloudProviderName = this.translations.Teams; break;
      case ExternalContentProviderSource.Hyarchis:
        if (this.selectedOption !== ExportMethods.Zip || this.isForAutoExport) {
          this.selectedOption = ExportMethods.Zip;
        }
        this.cloudProviderName = this.translations.Hyarchis; break;
    }

    const allowedProviders = [
      ExternalContentProviders.MsGraph,
      ExternalContentProviders.Hyarchis,
      ExternalContentProviders.PinkwebDossier,
      ExternalContentProviders.Afas
    ];
    this.hasSelectedProvider = allowedProviders.includes(this.provider);
    this.checkAccess();
  }

  /** Loads the contents of a folder by ID and updates the grid and breadcrumb trail. */
  showFolders(parent: any, id: any, name: any) {
    if (!this.isHyarchisSource) {
      this.searchTerm = '';
    }
    this.selectedExportId = id;
    this.currentFolderId = id;
    this.parentFolderId = parent ?? '';
    // When entering a drive root directly (parent is null but id is a drive), use the raw drive ID so search has a valid drive
    if (!this.parentFolderId && id?.startsWith('drive_')) {
      this.parentFolderId = id.substring('drive_'.length);
    }

    if (this.source === ExternalContentProviderSource.SharePoint && id?.startsWith('site_')) {
      this.currentSite = id;
      id = '';
    }

    if (this.isHyarchisSource && !this.hasMinimumSearchLength(this.searchTerm)) {
      // Hyarchis has no plain folder browsing; without a valid (>= 3 char) search term there is
      // nothing to load yet over the network. The selection above is still recorded, so once the
      // user types a valid search term, searchCurrent() runs against the item that was just
      // clicked instead of a stale/empty folder — and this must never surface as an error.
      this.isNavigating = false;
      return;
    }
    this.isNavigating = true;

    const addChildrenToId = (data: any[]) => {
      const folders = data.filter(item => item.isFolder);
      this.addChildrenRecursive(this.folderTree, this.currentFolderId, folders);
    };

    const updateGridAndCrumbs = (data: any[], id: string, name: string, parent: any) => {
      this.isNavigating = false;
      this.folderGrid = data;
      addChildrenToId(data);

      if (!this.selectedDirectory.some(item => item.id === id)) {
        const selectedFolder = this.findInTreeWithParent(this.folderTree, id);
        if (this.selectedDirectory.some(item => item.parent === selectedFolder?.parent?.id)) {
          this.removeCrumb(this.selectedDirectory.findIndex(item => item.parent === selectedFolder?.parent?.id));
        }
        this.addCrumb(selectedFolder?.node.name, selectedFolder?.node.id, selectedFolder?.node?.parent);
        this.isLoading = false;
      } else {
        const crumbIndex = this.selectedDirectory.findIndex(item => item.id === id);
        if (crumbIndex !== -1) this.removeCrumb(crumbIndex + 1);
      }
      setTimeout(() => this.scrollToNode$.next(id), 0);
    };

    const indexInSelected = this.selectedDirectory.findIndex(dir => dir.id === id);
    if (indexInSelected !== -1 && parent) {
      this.cloudLocationsApiClient.getParentChildContents(this.provider, this.source, this.currentSite, parent, id, this.purposeType, this.searchTerm)
        .subscribe((data: CloudLocation[]) => {
          this.isNavigating = false;
          this.folderGrid = data;
          addChildrenToId(data);
          this.removeCrumb(indexInSelected + 1);
        });
      return;
    }

    if (parent && parent !== '') {
      this.cloudLocationsApiClient.getParentChildContents(this.provider, this.source, this.currentSite, parent, id, this.purposeType, this.searchTerm)
        .subscribe((data: CloudLocation[]) => updateGridAndCrumbs(data, id || this.currentSite, name, parent));
      return;
    }

    this.cloudLocationsApiClient.getChildContents(this.provider, this.source, this.currentSite, id, this.purposeType, this.searchTerm)
      .subscribe((data: CloudLocation[]) => updateGridAndCrumbs(data, id || this.currentSite, name, parent));
  }

  /** True when the user has typed a search term in MsGraph mode; used to switch click behaviour. */
  get isSearchActive(): boolean {
    return this.searchTerm.trim().length > 0 && this.provider === ExternalContentProviders.MsGraph;
  }

  /** True when the given search term is long enough to search. Hyarchis requires at least 3 characters;
   *  other providers only require a non-empty term. */
  private hasMinimumSearchLength(term: string): boolean {
    const trimmed = term?.trim() ?? '';
    if (this.isHyarchisSource) return trimmed.length >= 3;
    return trimmed.length > 0;
  }

  /** Navigates the tree to the location of a search-result folder, expanding all ancestor nodes. */
  navigateToSearchResult(item: any) {
    const driveId = item.parent; // raw drive ID (no prefix)
    this.isNavigating = true;
    if (!this.isHyarchisSource) {
      this.searchTerm = '';
    }

    this.cloudLocationsApiClient.getAncestors(this.provider, this.source, this.currentSite, driveId, item.id)
      .subscribe({
        next: ({ siteId, teamId, chain }: { siteId?: string, teamId?: string, chain: any[] }) => {
          if (!chain?.length) {
            this.isNavigating = false;
            this.showFolders(item.parent, item.id, item.name);
            return;
          }
          this.removeAllCrumbs();

          const isTeams = this.source === ExternalContentProviderSource.Teams;
          const isSharePointNoSite = this.source === ExternalContentProviderSource.SharePoint && !this.currentSite;
          const driveNodeId = 'drive_' + driveId;
          const driveRequest$ = isTeams
            ? null
            : this.cloudLocationsApiClient.getChildContents(this.provider, this.source, this.currentSite, driveNodeId, this.purposeType, this.searchTerm);
          const folderRequests$ = chain.map(ancestor =>
            this.cloudLocationsApiClient.getParentChildContents(this.provider, this.source, this.currentSite, driveId, ancestor.id, this.purposeType, this.searchTerm)
          );
          const requests$ = [driveRequest$, ...folderRequests$].filter((r): r is Observable<CloudLocation[]> => r !== null);

          forkJoin(requests$).subscribe({
            next: (results: any[][]) => {
              let idx = 0;
              if (driveRequest$) {
                const driveRootFolders = (results[idx++] as any[]).filter((c: any) => c.isFolder);
                if (isSharePointNoSite) {
                  // Try to find the owning site using flexible matching — parentReference.siteId may use
                  // a shorter format (e.g. just a GUID) compared to the compound IDs in folderTree.
                  const foundSite = siteId
                    ? this.folderTree.find((n: any) => n.id === 'site_' + siteId || (siteId.length > 10 && n.id.includes(siteId)))
                    : null;
                  if (foundSite) {
                    this.addChildrenRecursive(this.folderTree, foundSite.id, [
                      { id: driveNodeId, name: '', isFolder: true, hasChildren: true, parent: foundSite.id, children: [] }
                    ]);
                    this.currentSite = foundSite.id;
                  }
                  // If the drive isn't in the tree yet (site not matched), add it at the top so
                  // the ancestor expansion below can still find and open it.
                  if (!this.findInTreeWithParent(this.folderTree, driveNodeId)) {
                    this.folderTree = [
                      { id: driveNodeId, name: '', isFolder: true, hasChildren: true, parent: '', children: [] },
                      ...this.folderTree
                    ];
                    if (siteId) this.currentSite = 'site_' + siteId;
                  }
                }
                this.addChildrenRecursive(this.folderTree, driveNodeId, driveRootFolders);
                const driveResult = this.findInTreeWithParent(this.folderTree, driveNodeId);
                if (driveResult?.node?.name) this.addCrumb(driveResult.node.name, driveResult.node.id, '');
              } else if (isTeams && chain.length > 0) {
                const teamNodeId = teamId ? 'team_' + teamId : null;
                const teamInTree = teamNodeId ? this.findInTreeWithParent(this.folderTree, teamNodeId) : null;
                if (teamInTree) {
                  // Attach chain[0] directly under the team so it doesn't appear at root level.
                  this.addChildrenRecursive(this.folderTree, teamNodeId!, [chain[0]]);
                  this.addCrumb(teamInTree.node.name, teamInTree.node.id, '');
                } else {
                  // Fallback: synthetic root node when team is not in the tree.
                  if (!this.findInTreeWithParent(this.folderTree, driveNodeId)) {
                    this.folderTree = [
                      { id: driveNodeId, name: '', isFolder: true, hasChildren: true, parent: '', children: [] },
                      ...this.folderTree
                    ];
                  }
                  this.addChildrenRecursive(this.folderTree, driveNodeId, [chain[0]]);
                }
              }
              chain.forEach((ancestor, i) => {
                this.addChildrenRecursive(this.folderTree, ancestor.id, results[idx + i].filter((c: any) => c.isFolder));
                this.addCrumb(ancestor.name, ancestor.id, driveId);
              });
              this.folderGrid = results[results.length - 1];
              this.currentFolderId = chain[chain.length - 1].id;
              this.selectedExportId = this.currentFolderId;
              this.parentFolderId = driveId;
              this.isNavigating = false;
              this.isLoading = false;
              if (this.isExportMode) this.listenToFolderSelection();
              setTimeout(() => this.scrollToNode$.next(this.currentFolderId), 0);
            },
            error: () => {
              this.isNavigating = false;
              this.showFolders(item.parent, item.id, item.name);
            }
          });
        },
        error: () => {
          this.isNavigating = false;
          this.showFolders(item.parent, item.id, item.name);
        }
      });
  }

  /** Navigates one level up; returns to the root when already at the first folder level. */
  pageUp() {
    const crumb = this.selectedDirectory[this.selectedDirectory.length - 2];
    this.removeCrumb(this.selectedDirectory.length - 1);
    if (this.selectedDirectory.length === 0) {
      this.folderGrid = this.folderTree;
      for (const item of this.folderTree) {
        if (item.children.length > 0) item.children = [];
      }
      this.parentFolderId = '';
      this.currentSite = '';
      if (!this.isHyarchisSource) {
        this.searchTerm = '';
      }
      return;
    }
    this.showFolders(crumb.parent ?? '', crumb.id, crumb.name);
    if (this.isExportMode) this.listenToFolderSelection();
  }

  /** Returns true when the given export method should be disabled for the current provider/mode. */
  disableExportOption(method: ExportMethods): boolean {
    if (this.isHyarchisSource && this.isForAutoExport) return true;
    if (this.isHyarchisSource && !this.isForAutoExport) return method === ExportMethods.Folder;
    return false;
  }

  /** True when the selected export method (Folder or Zip) requires a file/folder name to be entered. */
  showFileNameInput(): boolean {
    return this.selectedOption === ExportMethods.Folder || this.selectedOption === ExportMethods.Zip;
  }

  /** True when Hyarchis is the source and every document to export has a checklist item assigned. */
  get hasCheckListItemSelected(): boolean {
    return this.isHyarchisSource && this.documentsToExport.every(doc => doc.checklistItemId != null && doc.checklistItemId >= 1);
  }

  /** True when the export method requires a file/folder name and none (or only whitespace) was entered. */
  get isFileNameRequiredAndMissing(): boolean {
    return this.showFileNameInput() && !this.fileName?.trim();
  }

  /** Single source of truth for whether the Export action should be disabled, used by every Export
   *  button in the modal (details panel and the collapsed file-browser footer) so they never drift apart. */
  get isExportDisabled(): boolean {
    return (!this.hasCheckListItemSelected && this.isHyarchisSource) || this.isFileNameRequiredAndMissing;
  }

  // ── Bestand selectie (upload) ──────────────────────────────────────────────

  /** Adds the file to selectedFiles if not already selected, or removes it if it is. */
  toggleCheckbox(fileId: string, fileName: string, parent: string) {
    const index = this.selectedFiles.findIndex(f => f.id === fileId);
    if (index === -1) {
      this.selectedFiles.push({ id: fileId, name: fileName, parent });
    } else {
      this.selectedFiles.splice(index, 1);
    }
  }

  /** Returns true when the given file ID is in the current selection. */
  isFileSelected(fileId: string): boolean {
    return this.selectedFiles.some(f => f.id === fileId);
  }

  /** Handles the native checkbox change event; respects allowMultiselect and updates the visual selected state. */
  onFileCheckItem(event: any, fileId: string, fileName: string, parent: string) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      if (!this.allowMultiselect) {
        this.selectedFiles.forEach(f => document.getElementById(f.id)?.classList.remove('selected'));
        this.selectedFiles = [];
      }
      document.getElementById(fileId)!.classList.add('selected');
      this.selectedFiles.push({ id: fileId, name: fileName, parent });
    } else {
      document.getElementById(fileId)!.classList.remove('selected');
      const index = this.selectedFiles.findIndex(f => f.id === fileId);
      if (index > -1) this.selectedFiles.splice(index, 1);
    }
  }

  /** Emits all selected files via importFiles$ and clears the selection. */
  import() {
    this.selectedFiles.forEach(file => {
      this.importFiles$.next({ provider: this.provider, id: file.parent, externalFiles: file.id, name: file.name });
    });
    this.selectedFiles = [];
  }

  // ── Export ─────────────────────────────────────────────────────────────────

  /** Fetches Hyarchis checklist data for the currently selected folder; no-op when not in export mode. */
  listenToFolderSelection() {
    if (!this.isExportMode || !this.isHyarchisSource)
      return;
    const item = this.createCloudItem();
    if (item == null
      || (item.parentId == null || item.parentId == "")
      || (item.folderId == null || item.folderId == "")
    ) {
      return;
    }
    item.source = this.source;
    this.cloudLocationsApiClient.prepareStoreParentExternalContent(item, this.requestId, this.fileName).subscribe(data => {
      this.checklistItems = data?.checklistItems;
      this.documentsToExport = data?.documents;
    });
  }

  /** Updates the selected export method and refreshes Hyarchis checklist data if needed. */
  changeExportMethod(event: any) {
    this.selectedOption = (event.target as HTMLInputElement).value as ExportMethods;
    this.listenToFolderSelection();
  }

  /** Sends the selected folder/files to the provider and shows a success or error toast. */
  export() {
    const exportItem = this.createCloudItem();
    const onSuccess = () => {
      this.toastService.showSuccess(this.translations.ExportTitle, this.translations.ExportBody);
      if (!exportItem.folderId) {
        this.showFolders(null, exportItem.parentId, this.selectedDirectory.map(x => x.name).join('/'));
      } else {
        this.showFolders(exportItem.parentId, exportItem.folderId, this.selectedDirectory.map(x => x.name).join('/'));
      }
    };
    const onError = () => {
      this.toastService.showError(this.translations.error, this.translations.RequestsExportError);
    };

    if (exportItem.provider === ExternalContentProviders.Hyarchis) {
      this.cloudLocationsApiClient.saveParentExternalContentForHyarchis(exportItem.hyarchisDTO!, ExternalContentProviders.Hyarchis, exportItem.parentId, exportItem.folderId)
        .subscribe({ next: onSuccess, error: onError });
    } else {
      this.cloudLocationsApiClient.saveParentExternalContentForMsGraph(exportItem, this.requestId, this.fileName)
        .subscribe({ next: onSuccess, error: onError });
    }
  }

  /** Saves the selected cloud location as the auto-export target on the request without exporting immediately. */
  prepareExportForAutoExport() {
    const cloudItem = this.createCloudItem();
    if (this.isHyarchisSource) {
      if (!cloudItem.hyarchisDTO || cloudItem.hyarchisDTO.documentsCheckListSelection?.length <= 0) {
        this.toastService.showErrorByKey("Toasts.HyarchisChecklistItemMissingContent");
        return;
      }
    }
    this.setAutoExportCloudItem(cloudItem);
  }

  // ── CloudItem ──────────────────────────────────────────────────────────────

  /** Builds a CloudItem from the current service state; used as the payload for export API calls. */
  createCloudItem(): CloudItem {
    let item = new CloudItem();
    const selectedFolder = this.findInTreeWithParent(this.folderTree, this.selectedExportId!);
    item.parentId = selectedFolder?.node.parent ?? selectedFolder?.node.id ?? selectedFolder?.parent?.id ?? '';
    item.provider = this.provider;
    item.folderId = this.selectedExportId !== item.parentId ? this.selectedExportId! : '';
    item.folderPath = this.selectedDirectory.map(x => x.name).join('/');
    item.source = this.source;
    item.providerName = this.cloudProviderName;
    item.exportMethod = this.selectedOption.toLocaleLowerCase();
    item.fileName = this.fileName;
    item.exportName = this.fileName;

    if (this.isHyarchisSource) {
      const hyarchisData = {} as StoreDocumentsHyarchisDto;
      item.hyarchisDTO = null;
      if (this.documentsToExport?.length > 0 && item.itemId == null) {
        item.itemId = this.documentsToExport[0].checklistItemId;
      }
      hyarchisData.request = { requestId: this.requestId, cloudItem: { ...item } } as ExportRequestModel;
      hyarchisData.request.exportName = this.fileName;
      hyarchisData.documentsCheckListSelection = this.documentsToExport;
      item.hyarchisDTO = hyarchisData;
    }

    return item;
  }

  /** Walks the folder tree depth-first and returns the node plus its parent, or undefined if not found. */
  findInTreeWithParent(folders: any[], folderId: string, parent: any = null): { node: any, parent: any } | undefined {
    for (const node of folders) {
      if (node.id === folderId) return { node, parent };
      if (node.children?.length > 0) {
        const found = this.findInTreeWithParent(node.children, folderId, node);
        if (found) return found;
      }
    }
    return undefined;
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /** Returns 'export' or 'import' based on the current mode; used as a query parameter in API calls. */
  private get purposeType(): string {
    return this.isExportMode ? 'export' : 'import';
  }

  /** Attaches the given CloudItem as the auto-export target on the current request, initialising the model if needed.
   *  When there is no request (org-level default path), closes the modal with the CloudItem as the result. */
  private setAutoExportCloudItem(cloudItem: CloudItem | null) {
    if (!this.request) {
      this.activeModal.close(cloudItem);
      return;
    }
    if (!this.request.autoExportModel) {
      this.request.autoExportModel = new BuilderRequestAutoExportSettingsModel();
      this.request.autoExportModel.requestGuid = this.request.id;
    }
    this.request.autoExportModel.enabled = true;
    this.request.autoExportModel.cloudItem = cloudItem;
  }

  /** Fetches root-level provider contents; on auth error starts the OAuth popup flow. */
  private checkAccess() {
    this.cloudLocationsApiClient.getContents(this.provider, this.source, this.purposeType).subscribe({
      next: (data: CloudLocation[]) => {
        this.errorOccured = false;
        this.isNavigating = false;
        this.folderTree = data;
        this.folderGrid = data;
        if (this.folderTree) this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isNavigating = false;
        this.errorOccured = true;
        if (this.provider === ExternalContentProviders.MsGraph) {
          this.authorizeMSGraph();
        } else if (this.provider === ExternalContentProviders.Hyarchis) {
          this.authorizeHyarchis();
        } else if (this.provider === ExternalContentProviders.PinkwebDossier && error.status === 412) {
          this.activeModal.close();
          this.toastService.showError(
            '' + this.translations.error + '',
            '' + this.translateParser.interpolate(this.translations.ProviderConnectError, { providerName: this.cloudProviderName }) + ''
          );
        }
      }
    });
  }

  /** Opens a popup for Microsoft Graph OAuth consent and retries checkAccess after authentication. */
  private authorizeMSGraph() {
    this.url = this.msgraphApiClient.createUrlForUser(this.userGuid);
    const handler = (event: any) => {
      if (event.data === 'authenticated') {
        window.removeEventListener('message', handler);
        event.source.close();
        window.close();
        try { this.checkAccess(); } catch (e: any) { this.loggingService.logException(e); }
      }
    };
    window.addEventListener('message', handler);
    window.open(this.url, undefined, 'popup=yes');
  }

  // ── Hyarchis ────────────────────────────────────────────────────────
  /**
   * Opens a popup for Hyarchis OAuth consent and retries checkAccess after authentication.
   * The backend builds the url (shared Hyarchis B2C or the organisation's own tenant), so it matches its token request.
   */
  private authorizeHyarchis() {
    this.cloudLocationsApiClient.getHyarchisAuthorizationUrl().subscribe({
      next: ({ url }) => {
        this.url = url;
        const handler = (event: any) => {
          if (event.data === 'authenticated') {
            window.removeEventListener('message', handler);
            event.source.close();
            window.close();
            try { this.checkAccess(); } catch (e: any) { this.loggingService.logException(e); }
          }
        };
        window.addEventListener('message', handler);
        window.open(this.url, undefined, 'popup=yes');
      },
      error: () => {
        this.toastService.showWarning(this.translations.HyarchisNotConfiguredHeader, this.translations.HyarchisNotConfiguredContent);
      }
    });
  }
  /** Recursively finds targetId in the tree and replaces its children; clears siblings' children to collapse them. */
  private addChildrenRecursive(list: any[], targetId: string, children: any[]): boolean {
    for (const item of list) {
      if (item.id === targetId) {
        list.forEach(sibling => {
          sibling.children = sibling === item ? children : [];
        });
        return true;
      }
      if (item.children && Array.isArray(item.children)) {
        if (this.addChildrenRecursive(item.children, targetId, children)) return true;
      }
    }
    return false;
  }

  /** Appends a new crumb to the breadcrumb trail. */
  private addCrumb(directory: string, id: string, parent: string) {
    this.selectedDirectory.push(new Crumb(directory, id, parent));
  }

  /** Removes all crumbs from the given index onwards; used when navigating up or sideways. */
  private removeCrumb(fromIndex: number) {
    this.selectedDirectory.splice(fromIndex);
  }

  /** Clears the entire breadcrumb trail; called when switching providers. */
  private removeAllCrumbs() {
    this.selectedDirectory = [];
  }

  /** Executes a search against the current provider using the debounced search term. */
  private searchCurrent(searchTerm: string): Observable<any> {
    if (!searchTerm.trim()) {
      this.isNavigating = false;
      return EMPTY;
    }
    if (this.isHyarchisSource && !this.hasMinimumSearchLength(searchTerm)) {
      // Hyarchis requires at least 3 characters to search; let the user keep typing without
      // ever showing an error — searchCurrent will re-run automatically once they do.
      this.isNavigating = false;
      return EMPTY;
    }
    const term = searchTerm.trim().toLowerCase();
    const filterByName = (data: any[]) =>
      (data ?? []).filter((item: any) => item.name?.toLowerCase().includes(term));

    this.isNavigating = true;

    let search$: Observable<any>;
    if (this.provider === ExternalContentProviders.Hyarchis) {
      search$ = this.cloudLocationsApiClient.getChildContents(this.provider, this.source, this.currentSite, this.currentFolderId, this.purposeType, searchTerm);
    } else {
      const site = this.source === ExternalContentProviderSource.SharePoint ? this.currentSite : undefined;
      // For Teams, parentFolderId at channel level is the raw team GUID (not a drive GUID).
      // Only folder-level items have the correct drive GUID as their parent.
      // Detect channel level via currentFolderId prefix and extract drive from grid items.
      let drive = this.parentFolderId;
      if (this.source === ExternalContentProviderSource.Teams) {
        if (this.currentFolderId?.startsWith('channel_')) {
          drive = (this.folderGrid as any[])?.[0]?.parent ?? '';
        } else if (this.currentFolderId?.startsWith('team_')) {
          drive = '';
        }
      }
      search$ = this.cloudLocationsApiClient.search(this.provider, this.source, drive, this.isExportMode, searchTerm, site);
    }

    return search$.pipe(
      tap(data => { this.folderGrid = filterByName(data as unknown as any[]); this.isNavigating = false; })
    );
  }

  /** Loads all UI translation strings used in toasts and labels. */
  private getTranslations() {
    this.translateService.get([
      'CloudLocations.ProviderConnectError',
      'CloudLocations.Afas',
      'CloudLocations.Msgraph',
      'CloudLocations.Microsoft365',
      'CloudLocations.OneDrive',
      'CloudLocations.SharePoint',
      'CloudLocations.Hyarchis',
      'CloudLocations.Teams',
      'CloudLocations.Pinkwebdossier',
      'Toasts.Error',
      'Toasts.ExportTitle',
      'Toasts.ExportBody',
      'Toasts.RequestsExportError',
      'Toasts.DocumentsExportError',
      'Toasts.HyarchisNotConfiguredHeader',
      'Toasts.HyarchisNotConfiguredContent'
    ]).subscribe(t => {
      this.translations.ProviderConnectError = t['CloudLocations.ProviderConnectError'];
      this.translations.Afas = t['CloudLocations.Afas'];
      this.translations.Msgraph = t['CloudLocations.Msgraph'];
      this.translations.Microsoft365 = t['CloudLocations.Microsoft365'];
      this.translations.OneDrive = t['CloudLocations.OneDrive'];
      this.translations.SharePoint = t['CloudLocations.SharePoint'];
      this.translations.Hyarchis = t['CloudLocations.Hyarchis'];
      this.translations.Teams = t['CloudLocations.Teams'];
      this.translations.Pinkwebdossier = t['CloudLocations.Pinkwebdossier'];
      this.translations.error = t['Toasts.Error'];
      this.translations.ExportTitle = t['Toasts.ExportTitle'];
      this.translations.ExportBody = t['Toasts.ExportBody'];
      this.translations.RequestsExportError = t['Toasts.RequestsExportError'];
      this.translations.DocumentsExportError = t['Toasts.DocumentsExportError'];
      this.translations.HyarchisNotConfiguredHeader = t['Toasts.HyarchisNotConfiguredHeader'];
      this.translations.HyarchisNotConfiguredContent = t['Toasts.HyarchisNotConfiguredContent'];
    });
  }

}
